import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useMemo,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import ProgressBar from "../../../components/ProgressBar";
import ImageMapper from "react-img-mapper";
import {
  clearTableSelectionApiStatus,
  submitSelections,
} from "../../../redux/processor/actions";
import CustomizedSnackbars from "../../../components/Snackbars/CustomizedSnackbars";
import { Box, IconButton, Tooltip } from "@mui/material";
import { CircularProgress } from "@mui/material";
import "./preMapData.scss";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

const TabelSelections = React.forwardRef(
  (
    {
      imageData,
      selectionType,
      imageMapperWidth,
      imageMapperHeight,
      onClicked,
      pageRotateAngle,
      onSubmitCrop,
      pdfAndPageDetails,
      currentPageNo,
      currentFileNo,
      onCropDragStateChange,
      isSpacePressed,
      isMousePanning,
      ...props
    },
    ref,
  ) => {
    const dispatch = useDispatch();
    const tableSelectionApiStatus = useSelector(
      (state) => state?.pro?.tableSelectionApiStatus,
    );
    let extractedTableData = useSelector(
      (state) => state?.pro?.extractedTableData,
    );
    let highlightBboxInPre = useSelector(
      (state) => state?.prePro?.highlightBboxInPre,
    );

    const [selectedArea, setSelectedArea] = useState({});
    const [showSubmitButton, setShowSubmitButton] = useState(false);
    const [showCropScreen, setShowCropScreen] = useState(true);
    const [imageLoadedInDiv, setImageLoadedInDiv] = useState(false);
    const [savedBBox, setSavedBBox] = useState(null);
    const [activeDrawBox, setActiveDrawBox] = useState(null);
    const imgContainerRef = useRef(null);
    const drawingStateRef = useRef({ isDrawing: false, startX: 0, startY: 0 });
    const drawnOriginalCropRef = useRef(null);

    // ─── FIX (clarity on zoom): force the rendered image onto its own GPU ───
    // compositing layer. PrismaZoom's double-click zoom animates a CSS
    // `transform: scale()` on an ancestor. Browsers sometimes promote that
    // animated element to a compositing layer and rasterize a SNAPSHOT of
    // it at the pre-zoom on-screen size, then just stretch that snapshot
    // for the duration of (and sometimes after) the animation — instead of
    // continuously re-sampling the image's actual decoded bitmap. This is
    // why clarity drops specifically "after zoom" even though the
    // underlying image already has plenty of real resolution (we verified
    // that earlier — native res for unrotated, renderScale-boosted canvas
    // for rotated). Explicitly promoting the <img> itself to its own layer
    // (translateZ + backfaceVisibility + willChange) tells the browser to
    // keep sampling from the real bitmap as the texture, rather than a
    // smaller cached snapshot taken at the wrapper's pre-zoom size.
    const gpuLayerHintStyle = {
      transform: "translateZ(0)",
      WebkitBackfaceVisibility: "hidden",
      backfaceVisibility: "hidden",
      willChange: "transform",
    };

    // ─── FIX (bbox drag vs pan conflict): safety-net reset ──────────────────
    // onCropDragStateChange(true)/(false) is meant to tell the parent to
    // disable PrismaZoom's panning while the user is actively dragging a
    // crop box (parent flips `allowPan` accordingly — see ImageViewer). The
    // normal path for turning it back ON is ReactCrop's onDragEnd. But if
    // the user releases the mouse/finger somewhere ReactCrop doesn't see as
    // a clean drag-end (e.g. drags out past the image edge, or the browser
    // swallows the mouseup), panning could stay stuck disabled. This global
    // listener is a safety net that always re-enables panning on ANY
    // mouseup/touchend, regardless of where it happens.
    useEffect(() => {
      const resetDragState = () => onCropDragStateChange?.(false);
      window.addEventListener("mouseup", resetDragState);
      window.addEventListener("touchend", resetDragState);
      return () => {
        window.removeEventListener("mouseup", resetDragState);
        window.removeEventListener("touchend", resetDragState);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ─── FIX (blurry / squeezed text): true aspect-ratio guard ───────────────
    // The parent may pass an imageMapperWidth/imageMapperHeight box whose
    // aspect ratio does NOT match the real image's aspect ratio (e.g. parent
    // passes 600x768 — a portrait 0.78:1 box — while the actual image is
    // 3506x2480, a landscape 1.41:1 scan). If we draw the source image
    // directly into that mismatched box, the image gets stretched
    // non-uniformly: width and height get scaled by DIFFERENT factors. That
    // non-uniform stretch is what makes scanned text look mushy/unreadable
    // — it's not just a downscale blur, characters are literally distorted
    // taller/thinner or shorter/wider than they should be.
    //
    // safeBaseDims recomputes the correct "original/unrotated" display box
    // by preserving the real image's aspect ratio, fitting it inside the
    // imageMapperWidth x imageMapperHeight box (letterboxed, not stretched).
    // Falls back to the raw props if imageData width/height aren't available
    // yet, so behavior is unchanged until we actually know the real ratio.
    const safeBaseDims = useMemo(() => {
      if (!imageData?.width || !imageData?.height) {
        return { width: imageMapperWidth, height: imageMapperHeight };
      }
      const srcRatio = imageData.width / imageData.height;
      const boxRatio = imageMapperWidth / imageMapperHeight;
      return srcRatio > boxRatio
        ? {
            width: imageMapperWidth,
            height: Math.round(imageMapperWidth / srcRatio),
          }
        : {
            width: Math.round(imageMapperHeight * srcRatio),
            height: imageMapperHeight,
          };
    }, [
      imageData?.width,
      imageData?.height,
      imageMapperWidth,
      imageMapperHeight,
    ]);

    // ─── FIX (blurry text ON ZOOM, rotated pages only): render canvas at ────
    // higher pixel density. PrismaZoom zooms via CSS `transform: scale()` —
    // it doesn't add pixel detail, it just stretches whatever pixels already
    // exist. When a page IS rotated we still have to pre-rotate via canvas
    // (see big comment below for why), and that canvas was previously only
    // ever rendered at the small DISPLAY size, throwing away all the extra
    // resolution from the source. renderScale tells that canvas to allocate
    // a bigger pixel buffer (closer to native resolution) instead, capped by
    // MAX_CANVAS_DIMENSION for memory/perf. NOTE: as of the native-resolution
    // fix further below, this only matters anymore when angle !== 0 — at 0°
    // we skip canvas entirely and use the source image natively, same as the
    // validator's ImageViewer.
    const MAX_CANVAS_DIMENSION = 2400;

    const renderScale = useMemo(() => {
      if (!imageData?.width || !imageData?.height) return 1;
      // How much extra resolution is actually available from the source
      const idealScale = Math.min(
        imageData.width / safeBaseDims.width,
        imageData.height / safeBaseDims.height,
      );
      // Don't render bigger than this cap, regardless of source resolution
      const maxAllowedScale = Math.min(
        MAX_CANVAS_DIMENSION / safeBaseDims.width,
        MAX_CANVAS_DIMENSION / safeBaseDims.height,
      );
      return Math.max(1, Math.min(idealScale, maxAllowedScale));
    }, [
      imageData?.width,
      imageData?.height,
      safeBaseDims.width,
      safeBaseDims.height,
    ]);

    // ─── FIX (Issue 1 & 2): canvas-based image pre-rotation ──────────────────
    // CSS `transform: rotate()` only rotates the VISUAL appearance — it does
    // NOT change the element's layout box or how ReactCrop reads pointer
    // coordinates. ReactCrop always computes crop x/y against the image's
    // unrotated layout rect, so when the image is visually spun via CSS,
    // drag positions never match what the user sees. Wrapping the rotation
    // on an outer div does not fix this either, since ReactCrop still reads
    // its own bounding box.
    //
    // The only robust fix FOR ROTATED PAGES: physically rotate the IMAGE
    // PIXELS using a <canvas>, then feed that ALREADY-ROTATED image (as a
    // data URL) into ReactCrop with NO css transform at all. ReactCrop then
    // operates on a normal, unrotated image — drag coordinates always match
    // visually.
    //
    // ─── FIX (native resolution when unrotated, "validator-style") ─────────
    // When pageRotateAngle is 0°, there's nothing to fix — ReactCrop's drag
    // math works fine directly against a plain, un-rotated <img>. In that
    // case we now skip the canvas pipeline ENTIRELY and just point straight
    // at the original imageData.imageUrl, sized only via CSS (safeBaseDims)
    // — exactly the same approach the validator's ImageViewer/ImageMapper
    // uses. The browser keeps the image's full native pixel data in memory
    // regardless of the small CSS display size, so when PrismaZoom zooms in
    // afterwards there's real detail to draw from — no re-rasterization, no
    // toDataURL, no quality loss, and it's faster too since we skip the
    // canvas draw + encode for the common (unrotated) case entirely.
    // We only fall back to the canvas-rotation + renderScale approach below
    // when the page is ACTUALLY rotated (90/180/270), since that's the only
    // case ReactCrop's drag coordinates need a pre-rotated image to stay
    // correct.
    const canvasRef = useRef(null);
    const cachedImageRef = useRef(null);
    const [isImageReady, setIsImageReady] = useState(false);

    // Preload image once when imageData.imageUrl changes
    useEffect(() => {
      if (!imageData?.imageUrl) {
        setIsImageReady(false);
        cachedImageRef.current = null;
        return;
      }
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = imageData.imageUrl;
      img.onload = () => {
        cachedImageRef.current = img;
        setIsImageReady(true);
      };
      img.onerror = () => {
        setIsImageReady(true);
      };
    }, [imageData?.imageUrl]);

    const angle = ((pageRotateAngle % 360) + 360) % 360;
    const isSwapped = angle === 90 || angle === 270;
    const rotatedDims = useMemo(() => {
      const baseW = safeBaseDims.width;
      const baseH = safeBaseDims.height;
      return {
        width: isSwapped ? baseH : baseW,
        height: isSwapped ? baseW : baseH,
      };
    }, [isSwapped, safeBaseDims.width, safeBaseDims.height]);

    // Draw rotated image synchronously onto canvas in < 2ms (no toDataURL delay)
    useLayoutEffect(() => {
      const canvas = canvasRef.current;
      const img = cachedImageRef.current;
      if (!canvas || !img) return;

      const baseW = safeBaseDims.width;
      const baseH = safeBaseDims.height;
      const canvasW = isSwapped ? baseH : baseW;
      const canvasH = isSwapped ? baseW : baseH;

      canvas.width = canvasW * renderScale;
      canvas.height = canvasH * renderScale;
      const ctx = canvas.getContext("2d");

      ctx.save();
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.scale(renderScale, renderScale);
      ctx.translate(canvasW / 2, canvasH / 2);
      ctx.rotate((angle * Math.PI) / 180);
      ctx.drawImage(img, -baseW / 2, -baseH / 2, baseW, baseH);
      ctx.restore();
    }, [
      angle,
      isSwapped,
      safeBaseDims.width,
      safeBaseDims.height,
      renderScale,
      isImageReady,
    ]);

    // ─── Map ORIGINAL (unrotated) backend coords → ROTATED display space ────
    // Backend coordinates in pdfAndPageDetails are always in the ORIGINAL,
    // unrotated image's pixel space (same space the parent's onSubmitCrop
    // scaleX/scaleY math assumes). But the image shown to the user is now
    // pre-rotated via canvas. So before placing the overlay box, we must
    // forward-rotate the original coords into rotated display space — this
    // is the mirror/inverse of mapRotatedCropToOriginal used on submit.
    const mapOriginalToRotatedDisplay = (origBox) => {
      // origBox is { x, y, width, height } already scaled to imageMapperWidth/Height
      // (i.e. in ORIGINAL unrotated display pixels, NOT raw PDF pixels)
      const angle = ((pageRotateAngle % 360) + 360) % 360;
      const { x, y, width, height } = origBox;

      const oW = safeBaseDims.width;
      const oH = safeBaseDims.height;

      let rotX, rotY, rotWidth, rotHeight;

      switch (angle) {
        case 90:
          // forward rotate 90° CW: point (x,y) in original -> (oH-(y+height), x) in rotated
          rotX = oH - (y + height);
          rotY = x;
          rotWidth = height;
          rotHeight = width;
          break;
        case 180:
          rotX = oW - (x + width);
          rotY = oH - (y + height);
          rotWidth = width;
          rotHeight = height;
          break;
        case 270:
          rotX = y;
          rotY = oW - (x + width);
          rotWidth = height;
          rotHeight = width;
          break;
        default: // 0
          rotX = x;
          rotY = y;
          rotWidth = width;
          rotHeight = height;
      }

      return { x: rotX, y: rotY, width: rotWidth, height: rotHeight };
    };

    useEffect(() => {
      drawnOriginalCropRef.current = null;
    }, [
      pdfAndPageDetails?.coordinates,
      currentPageNo,
      currentFileNo,
      imageData?.imageUrl,
    ]);

    // Keep Pre-saved and Pro-saved coordinates in the same bbox state.
    // Previously, two separate effects called setSavedBBox independently;
    // whichever effect ran last replaced the other source's coordinates.
    useLayoutEffect(() => {
      const isSamePageAndFile = (source) =>
        source?.pageNo != null &&
        source?.fileNo != null &&
        String(source.pageNo) === String(currentPageNo) &&
        String(source.fileNo) === String(currentFileNo);

      const normalizeCoordinates = (coordinates) => {
        if (!Array.isArray(coordinates)) return [];

        const scaleX = safeBaseDims.width / (imageData?.width || 1);
        const scaleY = safeBaseDims.height / (imageData?.height || 1);

        return coordinates
          .filter(
            (bbox) =>
              Array.isArray(bbox) &&
              bbox.length >= 4 &&
              bbox.slice(0, 4).every((value) => Number.isFinite(Number(value))),
          )
          .map((bbox) => ({
            x: Number(bbox[0]) * scaleX,
            y: Number(bbox[1]) * scaleY,
            width: (Number(bbox[2]) - Number(bbox[0])) * scaleX,
            height: (Number(bbox[3]) - Number(bbox[1])) * scaleY,
          }))
          .filter((bbox) => bbox.width > 0 && bbox.height > 0)
          .map(mapOriginalToRotatedDisplay);
      };

      // Pro coordinates come from pdfAndPageDetails; Pre coordinates come
      // from highlightBboxInPre. Both are converted using the same mapping.
      const proBoxes = isSamePageAndFile(pdfAndPageDetails)
        ? normalizeCoordinates(pdfAndPageDetails?.coordinates)
        : [];

      const preBoxes = isSamePageAndFile(highlightBboxInPre)
        ? normalizeCoordinates(highlightBboxInPre?.coordinates)
        : [];

      // Preserve the locally drawn Pro crop as well as the saved coordinates.
      const locallyDrawnBox = drawnOriginalCropRef.current
        ? mapOriginalToRotatedDisplay(drawnOriginalCropRef.current)
        : null;

      const combinedBoxes = [...preBoxes, ...proBoxes];
      if (
        locallyDrawnBox &&
        !combinedBoxes.some(
          (box) =>
            Math.abs(box.x - locallyDrawnBox.x) < 0.5 &&
            Math.abs(box.y - locallyDrawnBox.y) < 0.5 &&
            Math.abs(box.width - locallyDrawnBox.width) < 0.5 &&
            Math.abs(box.height - locallyDrawnBox.height) < 0.5,
        )
      ) {
        combinedBoxes.push(locallyDrawnBox);
      }

      setSavedBBox(combinedBoxes);

      if (locallyDrawnBox && selectionType === "table") {
        setSelectedArea(locallyDrawnBox);
      }
    }, [
      pdfAndPageDetails?.coordinates,
      pdfAndPageDetails?.pageNo,
      pdfAndPageDetails?.fileNo,
      highlightBboxInPre?.coordinates,
      highlightBboxInPre?.pageNo,
      highlightBboxInPre?.fileNo,
      currentPageNo,
      currentFileNo,
      pageRotateAngle,
      imageMapperWidth,
      imageMapperHeight,
      imageData?.width,
      imageData?.height,
      imageData?.imageUrl,
      safeBaseDims.width,
      safeBaseDims.height,
      selectionType,
    ]);

    // ─── selectionType changes ────────────────────────────────────────────────
    useEffect(() => {
      if (selectionType === "table") {
        setShowSubmitButton(false);
      } else if (selectionType === "page") {
        const selectedAreaForFullPage = {
          unit: "px",
          x: 5,
          y: 5,
          height: rotatedDims.height - 10,
          width: rotatedDims.width - 10,
        };
        setSelectedArea({ ...selectedAreaForFullPage });
        setShowSubmitButton(true);
      }
      setShowCropScreen(true);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectionType, rotatedDims.width, rotatedDims.height]);

    useEffect(() => {
      dispatch(clearTableSelectionApiStatus());
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (tableSelectionApiStatus?.apiSuccess === true) {
        setShowCropScreen(false);
      }
    }, [tableSelectionApiStatus]);

    // ─── Ref exposed to parent ────────────────────────────────────────────────
    const onChangePdforPageWhenTableSelectin = () => {
      setShowCropScreen(true);
    };

    const onChangeCellInPreClearPreBBOxSelection = () => {
      setShowCropScreen(true);
      setSavedBBox(null);
      setSelectedArea({});
      setShowSubmitButton(false);
    };

    React.useImperativeHandle(ref, () => ({
      onChangePdforPageWhenTableSelectin,
      onChangeCellInPreClearPreBBOxSelection,
    }));

    // ─── Un-rotate crop coords before sending up ─────────────────────────────
    const mapRotatedCropToOriginal = (rotatedCrop) => {
      const angle = ((pageRotateAngle % 360) + 360) % 360;
      const { x, y, width, height } = rotatedCrop;

      // rotatedDims is the canvas/display size the crop was drawn on
      const rW = rotatedDims.width;
      const rH = rotatedDims.height;

      let origX, origY, origWidth, origHeight;

      switch (angle) {
        case 90:
          // rotated 90° CW: original top-left maps from rotated bottom-left
          origX = y;
          origY = rW - (x + width);
          origWidth = height;
          origHeight = width;
          break;
        case 180:
          origX = rW - (x + width);
          origY = rH - (y + height);
          origWidth = width;
          origHeight = height;
          break;
        case 270:
          origX = rH - (y + height);
          origY = x;
          origWidth = height;
          origHeight = width;
          break;
        default: // 0
          origX = x;
          origY = y;
          origWidth = width;
          origHeight = height;
      }

      return { x: origX, y: origY, width: origWidth, height: origHeight };
    };

    // ─── Zoom-Aware Box Drawing Handlers ──────────────────────────────────────
    const isDrawingMode = selectionType !== "page";
    const currentCursor = isSpacePressed
      ? isMousePanning
        ? "grabbing"
        : "grab"
      : isDrawingMode
        ? "crosshair"
        : "grab";

    const handleDrawMouseDown = (e) => {
      // Only left click initiates draw (button 0) and only in draw mode, when not space-panning
      if (!isDrawingMode || e.button !== 0 || isSpacePressed) return;

      const container = imgContainerRef.current;
      if (!container) return;

      e.preventDefault();
      e.stopPropagation();

      const rect = container.getBoundingClientRect();
      const zoomScale = rect.width / (rotatedDims.width || 1) || 1;

      const startX = Math.max(
        0,
        Math.min(rotatedDims.width, (e.clientX - rect.left) / zoomScale),
      );
      const startY = Math.max(
        0,
        Math.min(rotatedDims.height, (e.clientY - rect.top) / zoomScale),
      );

      drawingStateRef.current = {
        isDrawing: true,
        startX,
        startY,
      };

      drawnOriginalCropRef.current = null;
      setActiveDrawBox({ x: startX, y: startY, width: 0, height: 0 });
      onCropDragStateChange?.(true);
    };

    const handleDrawTouchStart = (e) => {
      if (!isDrawingMode || !e.touches?.[0] || isSpacePressed) return;
      const touch = e.touches[0];
      const container = imgContainerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const zoomScale = rect.width / (rotatedDims.width || 1) || 1;

      const startX = Math.max(
        0,
        Math.min(rotatedDims.width, (touch.clientX - rect.left) / zoomScale),
      );
      const startY = Math.max(
        0,
        Math.min(rotatedDims.height, (touch.clientY - rect.top) / zoomScale),
      );

      drawingStateRef.current = {
        isDrawing: true,
        startX,
        startY,
      };

      drawnOriginalCropRef.current = null;
      setActiveDrawBox({ x: startX, y: startY, width: 0, height: 0 });
      onCropDragStateChange?.(true);
    };

    useEffect(() => {
      const handleWindowMouseMove = (e) => {
        if (!drawingStateRef.current?.isDrawing || !imgContainerRef.current)
          return;
        e.preventDefault();

        const container = imgContainerRef.current;
        const rect = container.getBoundingClientRect();
        const zoomScale = rect.width / (rotatedDims.width || 1) || 1;

        const curX = Math.max(
          0,
          Math.min(rotatedDims.width, (e.clientX - rect.left) / zoomScale),
        );
        const curY = Math.max(
          0,
          Math.min(rotatedDims.height, (e.clientY - rect.top) / zoomScale),
        );

        const { startX, startY } = drawingStateRef.current;
        const x = Math.min(startX, curX);
        const y = Math.min(startY, curY);
        const width = Math.abs(curX - startX);
        const height = Math.abs(curY - startY);

        setActiveDrawBox({ x, y, width, height });
      };

      const handleWindowTouchMove = (e) => {
        if (
          !drawingStateRef.current?.isDrawing ||
          !imgContainerRef.current ||
          !e.touches?.[0]
        )
          return;
        const touch = e.touches[0];
        const container = imgContainerRef.current;
        const rect = container.getBoundingClientRect();
        const zoomScale = rect.width / (rotatedDims.width || 1) || 1;

        const curX = Math.max(
          0,
          Math.min(rotatedDims.width, (touch.clientX - rect.left) / zoomScale),
        );
        const curY = Math.max(
          0,
          Math.min(rotatedDims.height, (touch.clientY - rect.top) / zoomScale),
        );

        const { startX, startY } = drawingStateRef.current;
        const x = Math.min(startX, curX);
        const y = Math.min(startY, curY);
        const width = Math.abs(curX - startX);
        const height = Math.abs(curY - startY);

        setActiveDrawBox({ x, y, width, height });
      };

      const handleWindowMouseUp = () => {
        if (!drawingStateRef.current?.isDrawing) return;
        drawingStateRef.current.isDrawing = false;
        onCropDragStateChange?.(false);

        setActiveDrawBox((currBox) => {
          if (currBox && currBox.width > 3 && currBox.height > 3) {
            const originalSpaceCrop = mapRotatedCropToOriginal(currBox);
            drawnOriginalCropRef.current = originalSpaceCrop;
            setSavedBBox((prevBoxes) => [
              ...(selectionType === "table" ? [] : prevBoxes || []),
              {
                x: currBox.x,
                y: currBox.y,
                width: currBox.width,
                height: currBox.height,
              },
            ]);
            if (selectionType === "table") {
              setSelectedArea(currBox);
              setShowSubmitButton(true);
            } else {
              onSubmitCrop?.({ selectedArea: originalSpaceCrop });
            }
          }
          return null;
        });
      };

      window.addEventListener("mousemove", handleWindowMouseMove, {
        passive: false,
      });
      window.addEventListener("touchmove", handleWindowTouchMove, {
        passive: false,
      });
      window.addEventListener("mouseup", handleWindowMouseUp);
      window.addEventListener("touchend", handleWindowMouseUp);
      window.addEventListener("touchcancel", handleWindowMouseUp);

      return () => {
        window.removeEventListener("mousemove", handleWindowMouseMove);
        window.removeEventListener("touchmove", handleWindowTouchMove);
        window.removeEventListener("mouseup", handleWindowMouseUp);
        window.removeEventListener("touchend", handleWindowMouseUp);
        window.removeEventListener("touchcancel", handleWindowMouseUp);
      };
    }, [
      rotatedDims.width,
      rotatedDims.height,
      pageRotateAngle,
      safeBaseDims.width,
      safeBaseDims.height,
      selectionType,
    ]);

    const handleSubmit = () => {
      const originalSpaceArea =
        drawnOriginalCropRef.current || mapRotatedCropToOriginal(selectedArea);

      if (selectionType === "page") {
        dispatch(
          submitSelections({
            name: imageData?.name,
            pageNo: imageData?.pageNo,
            imageUrl: imageData?.imageUrl,
            extract_table_only: false,
            pageRotateAngle: pageRotateAngle,
          }),
        );
      } else if (selectionType === "table") {
        dispatch(
          submitSelections({
            name: imageData?.name,
            pageNo: imageData?.pageNo,
            imageUrl: imageData?.imageUrl,
            extract_table_only: true,
            pageRotateAngle: pageRotateAngle,
            coords: [
              originalSpaceArea?.x,
              originalSpaceArea?.y,
              originalSpaceArea?.width,
              originalSpaceArea?.height,
            ],
          }),
        );
      } else {
        onSubmitCrop?.({ selectedArea: originalSpaceArea });
      }
      setShowSubmitButton(false);
    };

    const handleCancel = () => {
      drawnOriginalCropRef.current = null;
      setSavedBBox(null);
      setSelectedArea({});
      setShowSubmitButton(false);
    };

    // ─── ImageMapper click ────────────────────────────────────────────────────
    const clicked = (area, index, e) => {
      const dataToUpdate = [...extractedTableData];
      dataToUpdate[index] = {
        ...extractedTableData[index],
        preFillColor: "#ff00002e",
        fillColor: "#ff00002e",
      };
      extractedTableData = dataToUpdate;
      onClicked(area, index, e);
    };

    const handleImageLoad = () => setImageLoadedInDiv(true);

    const buttonPosition = {
      top:
        selectionType === "page"
          ? 10
          : (selectedArea?.y ?? 0) + (selectedArea?.height ?? 0) + 15,
      left: (selectedArea?.x ?? 0) + (selectedArea?.width ?? 0) - 20,
    };

    // ─── Shared bbox overlay renderer ─────────────────────────────────────────
    const BBoxOverlay = () =>
      savedBBox?.map((bbox, index) => (
        <div
          key={index}
          style={{
            position: "absolute",
            left: `${bbox.x}px`,
            top: `${bbox.y}px`,
            width: `${bbox.width}px`,
            height: `${bbox.height}px`,
            border: "2px solid #2302022e",
            backgroundColor: "#ff00002e",
            boxSizing: "border-box",
            pointerEvents: "none",
            zIndex: 1000,
          }}
        />
      ));

    const ActiveDrawOverlay = () => {
      const boxToRender =
        activeDrawBox ||
        (selectionType === "page"
          ? selectedArea
          : showSubmitButton && selectionType === "table"
            ? selectedArea
            : null);

      if (!boxToRender || !boxToRender.width || !boxToRender.height)
        return null;

      return (
        <div
          style={{
            position: "absolute",
            left: `${boxToRender.x}px`,
            top: `${boxToRender.y}px`,
            width: `${boxToRender.width}px`,
            height: `${boxToRender.height}px`,
            border: "2px solid #230202",
            backgroundColor: "#ff00002e",
            boxSizing: "border-box",
            pointerEvents: "none",
            zIndex: 1001,
          }}
        />
      );
    };

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
      <>
        {showCropScreen ? (
          <div
            className="p-relative"
            style={{
              width: rotatedDims.width,
              height: rotatedDims.height,
              display: "inline-block",
            }}
          >
            <div
              id="container"
              style={{
                cursor: currentCursor,
                position: "relative",
                width: rotatedDims.width,
                height: rotatedDims.height,
              }}
            >
              {!isImageReady ? (
                <Box
                  className="premap-loader-container"
                  style={{
                    width: safeBaseDims.width,
                    height: safeBaseDims.height,
                  }}
                >
                  <CircularProgress />
                  <span className="premap-loader-style">Loading...</span>
                </Box>
              ) : (
                <div
                  ref={imgContainerRef}
                  style={{
                    position: "relative",
                    width: rotatedDims.width,
                    height: rotatedDims.height,
                    cursor: currentCursor,
                    userSelect: "none",
                  }}
                  onMouseDown={handleDrawMouseDown}
                  onTouchStart={handleDrawTouchStart}
                >
                  <canvas
                    ref={canvasRef}
                    style={{
                      width: `${rotatedDims.width}px`,
                      height: `${rotatedDims.height}px`,
                      display: "block",
                      pointerEvents: "none",
                      ...gpuLayerHintStyle,
                    }}
                  />
                  <BBoxOverlay />
                  <ActiveDrawOverlay />
                </div>
              )}
            </div>

            {/* Submit / Cancel buttons */}
            {showSubmitButton &&
              selectedArea?.width > 0 &&
              selectedArea?.height > 0 && (
                <div
                  style={{
                    position: "absolute",
                    top: buttonPosition.top,
                    left: buttonPosition.left,
                    transform: "translate(-50%, -50%)",
                    display: "flex",
                    gap: "6px",
                    zIndex: 10,
                  }}
                >
                  <Tooltip title="Submit">
                    <IconButton
                      size="small"
                      onClick={handleSubmit}
                      style={{
                        background: "#4CD426",
                        color: "#fff",
                        width: 20,
                        height: 20,
                      }}
                    >
                      <CheckIcon style={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Cancel">
                    <IconButton
                      size="small"
                      onClick={handleCancel}
                      style={{
                        background: "#f44336",
                        color: "#fff",
                        width: 20,
                        height: 20,
                      }}
                    >
                      <CloseIcon style={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </div>
              )}

            {tableSelectionApiStatus?.loading && <ProgressBar />}
          </div>
        ) : (
          // ── ImageMapper (post-extraction) — unchanged, still uses CSS
          // rotation since ImageMapper isn't a drag-to-draw tool, just
          // click-based area selection where this mismatch doesn't apply.
          <div
            id="container"
            style={{
              transform: `rotate(${pageRotateAngle}deg)`,
              transition: "transform 150ms ease",
              cursor: selectionType !== "page" ? "crosshair" : "grab",
              left: [
                90, 270, 450, 630, 810, 990, 1170, 1350, 1530, 1710, 1890,
              ].includes(pageRotateAngle)
                ? "14%"
                : 0,
              position: "relative",
            }}
          >
            <img
              alt="Map_Image"
              src={imageData.imageUrl}
              onLoad={handleImageLoad}
              className="d-none"
            />
            {imageLoadedInDiv ? (
              <ImageMapper
                src={imageData.imageUrl}
                map={{ name: imageData?.name, areas: [...extractedTableData] }}
                width={imageMapperWidth}
                height={imageMapperHeight}
                onClick={(area, index, e) => clicked(area, index, e)}
                imgWidth={imageMapperWidth}
                natural={true}
                responsive={true}
                parentWidth={imageMapperWidth}
              />
            ) : (
              <Box className="premap-loader-container">
                <CircularProgress />
                <span className="premap-loader-style">Loading...</span>
              </Box>
            )}
          </div>
        )}

        {/* Snackbars */}
        {tableSelectionApiStatus?.apiSuccess === true && (
          <CustomizedSnackbars
            severity="success"
            state={tableSelectionApiStatus?.apiSuccess}
            text={tableSelectionApiStatus?.message}
            handleClose={() => dispatch(clearTableSelectionApiStatus())}
          />
        )}
        {tableSelectionApiStatus?.apiError === true && (
          <CustomizedSnackbars
            severity="error"
            state={tableSelectionApiStatus?.apiError}
            text={tableSelectionApiStatus?.message}
            handleClose={() => dispatch(clearTableSelectionApiStatus())}
          />
        )}
      </>
    );
  },
);

export default TabelSelections;
